import React, { createContext, useContext, ReactNode } from 'react';
import { IPaginatedResultDTO } from '@common-frontend-submodule/interfaces/common/ipaginated-result.interface';
import { IClientSourceDTO } from '@common-frontend-submodule/interfaces/client-source/iclient-source.interface';
import { IClientSourceCreationDTO } from '@common-frontend-submodule/interfaces/client-source/iclient-source-creation.interface';
import { IClientSourceUpdateDTO } from '@common-frontend-submodule/interfaces/client-source/iclient-source-update.interface';
import { IGetClientSourcesRequestDTO } from '@common-frontend-submodule/interfaces/client-source/iget-client-sources-request.interface';
import { IApiResponseGenericDTO } from '@common-frontend-submodule/interfaces/common/iapi-response.interface';
import { ClientSourceRoutes } from '@common-frontend-submodule/enums/routes/client-sources-routes.enum';
import ApiClient from '../../lib/api-client';

const apiClient = ApiClient.getInstance()

interface ClientSourceContextProps {
  getAll: () => Promise<IApiResponseGenericDTO<IClientSourceDTO[]>>;
  getById: (clientSourceId: string) => Promise<IApiResponseGenericDTO<IClientSourceDTO>>;
  filter: (model: IGetClientSourcesRequestDTO) => Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IClientSourceDTO>>>;
  create: (model: IClientSourceCreationDTO) => Promise<IApiResponseGenericDTO<IClientSourceDTO>>;
  update: (clientSourceId: string, model: IClientSourceUpdateDTO) => Promise<IApiResponseGenericDTO<IClientSourceDTO>>;
  delete: (clientSourceId: string) => Promise<IApiResponseGenericDTO<boolean>>;
}

const ClientSourceContext = createContext<ClientSourceContextProps | undefined>(undefined);

export const ClientSourceProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const getAll = async (): Promise<IApiResponseGenericDTO<IClientSourceDTO[]>> => {
    return await apiClient.getRequest<IClientSourceDTO[]>(`${ClientSourceRoutes.BasePrefix}/${ClientSourceRoutes.All}`);
  };

  const getById = async (clientSourceId: string): Promise<IApiResponseGenericDTO<IClientSourceDTO>> => {
    return await apiClient.getRequest<IClientSourceDTO>(`${ClientSourceRoutes.BasePrefix}/${clientSourceId}`);
  };

  const filter = async (model: IGetClientSourcesRequestDTO): Promise<IApiResponseGenericDTO<IPaginatedResultDTO<IClientSourceDTO>>> => {
    return await apiClient.postRequest<IGetClientSourcesRequestDTO, IPaginatedResultDTO<IClientSourceDTO>>(
      `${ClientSourceRoutes.BasePrefix}/${ClientSourceRoutes.List}`,
      model
    );
  };

  const create = async (model: IClientSourceCreationDTO): Promise<IApiResponseGenericDTO<IClientSourceDTO>> => {
    return await apiClient.postRequest<IClientSourceCreationDTO, IClientSourceDTO>(ClientSourceRoutes.BasePrefix, model);
  };

  const update = async (clientSourceId: string, model: IClientSourceUpdateDTO): Promise<IApiResponseGenericDTO<IClientSourceDTO>> => {
    return await apiClient.putRequest<IClientSourceUpdateDTO, IClientSourceDTO>(`${ClientSourceRoutes.BasePrefix}/${clientSourceId}`, model);
  };

  const deleteClientSource = async (clientSourceId: string): Promise<IApiResponseGenericDTO<boolean>> => {
    return await apiClient.deleteRequest<null, boolean>(`${ClientSourceRoutes.BasePrefix}/${clientSourceId}`);
  };

  return (
    <ClientSourceContext.Provider value={{ getAll, getById, filter, create, update, delete: deleteClientSource }}>
      {children}
    </ClientSourceContext.Provider>
  );
};

export const useClientSourceStore = () => {
  const context = useContext(ClientSourceContext);
  if (!context) {
    throw new Error('useClientSourceStore must be used within a ClientSourceProvider');
  }
  return context;
};
